import { useCallback, useEffect, useMemo, useState } from "react";
import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";
import { TUTOR_DETAILS_KEYS } from "./tutorHubDetails";

export default function useTutorHub() {
    const [userInfo, setUserInfo] = useState(null);
    const [libraryAll, setLibraryAll] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const reload = useCallback(async () => {
        const next = await UserService.getUserInfo();
        const stories = await ApiService.getData("/v1/tutor/stories");
        setUserInfo(next || null);
        setLibraryAll(Array.isArray(stories) ? stories : []);
        return next;
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const next = await UserService.getUserInfo();
                const stories = await ApiService.getData("/v1/tutor/stories");
                if (!cancelled) {
                    setUserInfo(next || null);
                    setLibraryAll(Array.isArray(stories) ? stories : []);
                    setError("");
                }
            } catch (err) {
                Logger.error("Tutor hub: failed to load user", err);
                if (!cancelled) {
                    setError("Could not load your tutor workspace.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const patchDetails = useCallback(async (patch) => {
        const cloudUserId = userInfo?.cloudUser?.id;
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        const nextPatch = { ...(patch || {}) };
        const hasLibrary = Object.prototype.hasOwnProperty.call(nextPatch, TUTOR_DETAILS_KEYS.library);
        const libraryPayload = nextPatch[TUTOR_DETAILS_KEYS.library];
        delete nextPatch[TUTOR_DETAILS_KEYS.library];
        const hasProfile = Object.keys(nextPatch).length > 0;

        setSaving(true);
        setError("");
        try {
            if (hasLibrary) {
                const saved = await ApiService.putData("/v1/tutor/stories", { stories: libraryPayload });
                setLibraryAll(Array.isArray(saved) ? saved : []);
            }
            let next = userInfo;
            if (hasProfile) {
                await UserService.updateUserProfile(cloudUserId, nextPatch);
                next = await UserService.getUserInfo();
                setUserInfo(next || null);
            }
            return next;
        } catch (err) {
            Logger.error("Tutor hub: failed to save", err);
            setError(err?.message || "Could not save.");
            throw err;
        } finally {
            setSaving(false);
        }
    }, [userInfo]);

    const details = useMemo(() => {
        const base = { ...(userInfo?.cloudUser?.details || {}) };
        delete base[TUTOR_DETAILS_KEYS.library];
        base[TUTOR_DETAILS_KEYS.library] = libraryAll;
        return base;
    }, [libraryAll, userInfo]);

    return {
        userInfo,
        cloudUser: userInfo?.cloudUser || null,
        details,
        loading,
        saving,
        error,
        setError,
        reload,
        patchDetails,
    };
}
